import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SpecraButton, SpecraCombobox, SpecraInput } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useId, useMemo, useRef, useState } from 'react';
import { useProgramContext } from '../../../../layouts/ProgramLayout';
import { ConfirmDeleteDialog } from '../../_shared/components/ConfirmDeleteDialog';
import { EntityFormDialog } from '../../_shared/components/EntityFormDialog';
import { EntityTable } from '../../_shared/components/EntityTable';
import { ScoreDetailDrawer } from '../../_shared/components/ScoreDetailDrawer';
import { SectionNote } from '../../_shared/components/SectionNote';
import { SourceChip } from '../../_shared/components/SourceChip';
import { CONTACT_TYPE_OPTIONS, isInternalContact, withCurrentSelectable } from '../../_shared/constants/contactTypes';
import { useTenantId } from '../../_shared/hooks/useTenantId';
import { contactHooks } from '../../Registries/hooks';
import { PROGRAM_WRITE_DENIED, useProgramWriteAccess } from '../programWriteAccess';
import { useTenantEnumValues } from '../referenceDataHooks';
import { programContactHooks } from '../relationshipHooks';
// Sentinel for the "create a brand-new contact" option in the contact select.
const NEW_CONTACT = '__new__';
// New customer POCs default to Government — the CO/COR/CS roles are the
// customer's contracting staff, not internal team members.
const EMPTY_FORM = {
    contactId: '',
    firstName: '',
    lastName: '',
    email: '',
    contactType: 'GOVERNMENT',
    phone: '',
    city: '',
    state: '',
    customerPocRoleId: '',
    customRole: ''
};
// A stored location is "City, ST". Split on the last comma so a city containing
// a comma still lands in `city`, and compose back into that canonical form.
function splitLocation(loc) {
    const idx = loc.lastIndexOf(',');
    if (idx < 0) return {
        city: loc.trim(),
        state: ''
    };
    return {
        city: loc.slice(0, idx).trim(),
        state: loc.slice(idx + 1).trim()
    };
}
function joinLocation(city, state) {
    const c = city.trim();
    const s = state.trim().toUpperCase();
    // A state without a city can't form "City, ST" and would round-trip back as a
    // city, so it's only appended when there is a city.
    return c ? s ? `${c}, ${s}` : c : '';
}
// True when a POC-role enum row is the "Other" (free-typed) option.
function isOtherRole(role) {
    return role?.code === 'Other' || role?.displayLabel === 'Other';
}
// Affiliation (spec 10 / Phase 3 slice 1): gov org, else company profile, else
// the legacy free-text organization, else the contact's own side: a
// government contact no office names is still the government's, never
// "Unaffiliated" (SAD-856). Shared by the Affiliation column and the
// grouping below so the two can never disagree.
function contactAffiliation(c) {
    return c?.governmentOrganizationName || c?.companyProfileName || c?.organization || (c?.contactType === 'GOVERNMENT' ? 'Government' : '—');
}
const UNAFFILIATED_GROUP = 'Unaffiliated';
// Groups rows by their affiliation label, in first-appearance order, with the
// no-affiliation bucket always pinned last — a defined home for it rather than
// a spot that moves around depending on where those contacts land in `items`.
// First-appearance order (not e.g. alphabetical or by-count) is what keeps
// group order from flickering as rows load or a contact's data changes.
function groupByAffiliation(items, contactById) {
    const order = [];
    const byLabel = new Map();
    for (const row of items){
        const affiliation = contactAffiliation(contactById.get(row.contactId));
        const label = affiliation === '—' ? UNAFFILIATED_GROUP : affiliation;
        const bucket = byLabel.get(label);
        if (bucket) {
            bucket.push(row);
        } else {
            byLabel.set(label, [
                row
            ]);
            order.push(label);
        }
    }
    const labels = order.filter((l)=>l !== UNAFFILIATED_GROUP);
    if (byLabel.has(UNAFFILIATED_GROUP)) labels.push(UNAFFILIATED_GROUP);
    return labels.map((label)=>({
            label,
            rows: byLabel.get(label) ?? []
        }));
}
function LabeledField({ id, label, required, children }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-1.5",
        children: [
            /*#__PURE__*/ _jsxs(Label, {
                htmlFor: id,
                children: [
                    label,
                    required && /*#__PURE__*/ _jsx("span", {
                        className: "ml-1 text-destructive",
                        children: "*"
                    })
                ]
            }),
            children
        ]
    });
}
export function ProgramContactsTab({ programId }) {
    const { canManageContacts, program } = useProgramContext();
    // UpsertProgramContact: update + area:programs. Delete is the separate
    // manage_contacts verb (canManageContacts). canEdit is update alone.
    const canWrite = useProgramWriteAccess(programId).roster;
    // Lets the SourceChip's "Open in document" action land on the ref's page.
    const zouProjectId = program?.zouProjectId ?? '';
    const deleteHintId = useId();
    const tenantId = useTenantId();
    const pocRoles = useTenantEnumValues('customer_poc_role');
    const contactsQuery = contactHooks.useList({
        companyId: tenantId
    });
    const contactUpsert = contactHooks.useUpsert();
    const { data, isLoading } = programContactHooks.useList({
        programId
    });
    const upsert = programContactHooks.useUpsert();
    const remove = programContactHooks.useDelete();
    const [formOpen, setFormOpen] = useState(false);
    const [deleting, setDeleting] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    // Holds a contact created earlier in THIS dialog session so a retry after a
    // failed program assignment reuses it rather than creating a second contact.
    const createdContactIdRef = useRef('');
    // Store only the selected id and DERIVE the record from the list — so an open
    // drawer always reflects the latest data after an inline-edit Save, without an
    // effect that re-syncs state (which infinite-loops on fresh-but-equal refs).
    const items = data?.items ?? [];
    const [detailId, setDetailId] = useState(null);
    const detail = detailId ? items.find((c)=>c.id === detailId) ?? null : null;
    // Whether the detail drawer opens in read or straight into edit (row Edit).
    const [detailMode, setDetailMode] = useState('read');
    // The contact selection for the drawer's inline edit — parent-controlled (not
    // RHF), mirroring RisksTab's owner picker, so picking a contact never remounts
    // the form. Seeded from the record on edit start.
    const [editContactId, setEditContactId] = useState('');
    const contacts = contactsQuery.data?.items ?? [];
    const contactById = useMemo(()=>{
        const m = new Map();
        for (const c of contacts)m.set(c.id, c);
        return m;
    }, [
        contacts
    ]);
    // Program contacts are the customer's POCs, so only GOVERNMENT/EXTERNAL
    // registry contacts are offered. The contact already on the assignment being
    // viewed stays selectable even if internal, so editing the role doesn't blank
    // or reassign the person. Internal team members belong on the Roles tab.
    const selectableContacts = useMemo(()=>withCurrentSelectable(contacts.filter((c)=>!isInternalContact(c)), detail?.contactId, contacts), [
        contacts,
        detail
    ]);
    // Searchable by name or email (SAD-863); the label is unchanged.
    const contactOptions = useMemo(()=>selectableContacts.map((c)=>({
                value: c.id,
                label: `${c.firstName} ${c.lastName}`.trim() || c.email,
                keywords: [
                    c.email,
                    c.corporateEmail,
                    c.governmentEmail
                ].filter((e)=>!!e)
            })), [
        selectableContacts
    ]);
    const roles = pocRoles.data ?? [];
    const selectedRole = roles.find((v)=>v.id === form.customerPocRoleId);
    const formIsOtherRole = isOtherRole(selectedRole);
    const isNewContact = form.contactId === NEW_CONTACT;
    const setField = (key, value)=>setForm((prev)=>({
                ...prev,
                [key]: value
            }));
    const resetForm = ()=>{
        setForm(EMPTY_FORM);
        createdContactIdRef.current = '';
    };
    // Picking a contact pre-fills the editable phone/location (and the new-contact
    // fields clear) so an existing contact's current values are the starting point.
    const selectContact = (id)=>{
        // Any change of the contact selection starts a fresh intent — drop a contact
        // created earlier in this session so re-picking "+ New contact" CREATEs a new
        // one rather than UPDATE-clobbering the earlier record. A straight retry never
        // reaches here (the select value is unchanged), so it still reuses the id.
        createdContactIdRef.current = '';
        if (id === NEW_CONTACT) {
            setForm((prev)=>({
                    ...prev,
                    contactId: id,
                    firstName: '',
                    lastName: '',
                    email: '',
                    contactType: 'GOVERNMENT',
                    phone: '',
                    city: '',
                    state: ''
                }));
            return;
        }
        const c = contactById.get(id);
        const loc = splitLocation(c?.location ?? '');
        setForm((prev)=>({
                ...prev,
                contactId: id,
                firstName: c?.firstName ?? '',
                lastName: c?.lastName ?? '',
                email: c?.email ?? '',
                contactType: c?.contactType || 'GOVERNMENT',
                phone: c?.phone ?? '',
                city: loc.city,
                state: loc.state
            }));
    };
    const openAdd = ()=>{
        resetForm();
        setFormOpen(true);
    };
    const columns = useMemo(()=>[
            {
                key: 'contactName',
                header: 'Contact',
                render: (row)=>{
                    const c = contactById.get(row.contactId);
                    const name = row.contactName || (c ? `${c.firstName} ${c.lastName}`.trim() : '') || '—';
                    // Extraction persists per-contact provenance keyed `Contact` /
                    // `{programId}/{email}` — email is whatever address string the
                    // extractor scraped, which is not always the derived primary, so
                    // the other labeled address is tried as a fallback.
                    const primaryEmail = c?.email || row.contactEmail || '';
                    const fallbackEmail = [
                        c?.governmentEmail,
                        c?.corporateEmail
                    ].find((e)=>e && e !== primaryEmail);
                    return /*#__PURE__*/ _jsxs("span", {
                        className: "inline-flex items-center gap-1",
                        children: [
                            name,
                            primaryEmail && /*#__PURE__*/ _jsx(SourceChip, {
                                targetObjectType: "Contact",
                                targetObjectId: `${programId}/${primaryEmail}`,
                                fallbackTargetObjectId: fallbackEmail ? `${programId}/${fallbackEmail}` : undefined,
                                projectId: zouProjectId
                            })
                        ]
                    });
                }
            },
            // Both labeled addresses (SAD-76): the denormalized single `contactEmail`
            // only ever showed one, and there was nowhere on the program to see the
            // other. Read straight off the joined registry contact — never derive or
            // write `email` here, it's server-derived from these two on every save.
            {
                key: 'contactEmails',
                header: 'Email',
                render: (row)=>{
                    const c = contactById.get(row.contactId);
                    const corporate = c?.corporateEmail || '';
                    const government = c?.governmentEmail || '';
                    if (!corporate && !government) return '—';
                    return /*#__PURE__*/ _jsxs("span", {
                        className: "flex flex-col gap-0.5",
                        "data-testid": Testids.deliveryProgramRel.programContactEmails,
                        children: [
                            corporate && /*#__PURE__*/ _jsxs("span", {
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-muted-foreground",
                                        children: "Corporate: "
                                    }),
                                    corporate
                                ]
                            }),
                            government && /*#__PURE__*/ _jsxs("span", {
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-muted-foreground",
                                        children: "Government: "
                                    }),
                                    government
                                ]
                            })
                        ]
                    });
                }
            },
            // Phone and Location are read from the joined registry contact — unlike
            // name/email, they aren't denormalized onto ProgramContact. The contact
            // list is currently returned unpaged, so the join is complete; if it is
            // ever capped, denormalize contact_phone/contact_location onto the proto
            // instead (follow-up) so these can't fall out of sync with Email.
            {
                key: 'phone',
                header: 'Phone #',
                render: (row)=>contactById.get(row.contactId)?.phone || '—'
            },
            {
                key: 'location',
                header: 'Location',
                render: (row)=>contactById.get(row.contactId)?.location || '—'
            },
            // Affiliation (spec 10 / Phase 3 slice 1): who this person belongs to —
            // gov org, else company profile (both server-resolved read helpers on
            // the joined registry contact), else the legacy free-text organization.
            // Same fallback order the Registries Contacts tab uses.
            {
                key: 'affiliation',
                header: 'Affiliation',
                render: (row)=>{
                    const label = contactAffiliation(contactById.get(row.contactId));
                    return /*#__PURE__*/ _jsx("span", {
                        "data-testid": Testids.deliveryProgramRel.programContactAffiliation,
                        children: label
                    });
                }
            },
            {
                key: 'customerPocRoleLabel',
                header: 'Role',
                // For the "Other" role the free-typed name lives in `role`, so surface it
                // rather than the generic label. Detect "Other" via the enum row (code or
                // label) to match the form, not a hard-coded label string.
                render: (row)=>{
                    const role = roles.find((v)=>v.id === row.customerPocRoleId);
                    if (isOtherRole(role) && row.role && row.role !== 'Other') return row.role;
                    return row.customerPocRoleLabel || row.role || '—';
                }
            }
        ], [
        contactById,
        roles,
        programId,
        zouProjectId
    ]);
    // UI/UX ask (SAD-76): section the table by company/agency affiliation, each
    // group under a collapsible header, rather than one long flat list. Grouping
    // reuses the exact fallback order the Affiliation column renders with, so a
    // row's group and its own Affiliation cell can never disagree.
    const contactGroups = useMemo(()=>groupByAffiliation(items, contactById), [
        items,
        contactById
    ]);
    const handleContactRowClick = (row)=>{
        setDetailMode('read');
        setDetailId(row.id);
    };
    const contactRowActions = (row)=>/*#__PURE__*/ _jsxs("div", {
            className: "flex gap-2 justify-end",
            children: [
                canWrite && /*#__PURE__*/ _jsx(SpecraButton, {
                    size: "sm",
                    variant: "ghost",
                    // Edit-in-place: open the drawer straight into edit mode; the
                    // drawer's onEditStart seeds the contact control from this record.
                    onClick: ()=>{
                        setDetailMode('edit');
                        setDetailId(row.id);
                    },
                    "data-testid": Testids.deliveryProgramRel.contactEdit,
                    children: "Edit"
                }),
                /*#__PURE__*/ _jsx(SpecraButton, {
                    size: "sm",
                    variant: "ghost",
                    "aria-disabled": !canManageContacts || undefined,
                    "aria-describedby": !canManageContacts ? deleteHintId : undefined,
                    className: "text-danger hover:bg-danger-muted hover:text-danger aria-disabled:cursor-not-allowed aria-disabled:opacity-50",
                    onClick: ()=>{
                        if (!canManageContacts) return;
                        setDeleting(row);
                    },
                    "data-testid": Testids.deliveryProgramRel.contactDelete,
                    children: "Delete"
                })
            ]
        });
    // The unified Add Contact submit (create-only now): assign an existing contact
    // (optionally writing back an edited phone/location) or mint a brand-new one,
    // then assign it to the program. Editing an existing assignment happens inline
    // in the drawer via submitContactEdit.
    const handleSubmit = async ()=>{
        if (!form.contactId) throw new Error('Select a contact or add a new one.');
        if (!form.customerPocRoleId) throw new Error('Select a customer POC role.');
        let contactId = form.contactId;
        if (isNewContact) {
            if (!form.firstName.trim() || !form.lastName.trim()) {
                throw new Error('A new contact needs a first and last name.');
            }
            // Create on the first attempt (empty id); on a retry after a failed
            // assignment, the ref holds the created id so this becomes an UPDATE —
            // edits made between attempts persist and no duplicate contact is created.
            const trimmedEmail = form.email.trim();
            // A contact minted here is a customer POC, so the single email input
            // maps to whichever labeled column matches the chosen type — email
            // itself is server-derived and must never be sent.
            const labeledEmail = form.contactType === 'GOVERNMENT' ? {
                governmentEmail: trimmedEmail
            } : {
                corporateEmail: trimmedEmail
            };
            const created = await contactUpsert.mutateAsync({
                id: createdContactIdRef.current,
                companyId: tenantId,
                firstName: form.firstName.trim(),
                lastName: form.lastName.trim(),
                title: '',
                ...labeledEmail,
                phone: form.phone.trim(),
                location: joinLocation(form.city, form.state),
                organization: '',
                contactType: form.contactType,
                companyProfileId: '',
                governmentOrganizationId: '',
                isActive: true
            });
            const newId = created?.id ?? '';
            if (!newId) throw new Error('Creating the contact did not return an id.');
            createdContactIdRef.current = newId;
            contactId = newId;
        } else {
            // Existing contact: if the user edited phone/location, write it back to the
            // registry record (the change is global, not program-scoped). UpsertContact
            // replaces the whole row, so this re-sends every field from the (up to 30s
            // stale) list cache — a concurrent edit to an untouched field can be lost
            // last-write-wins. The real fix is a partial-update RPC; tracked separately.
            const c = contactById.get(contactId);
            // Compare the City/State against the values prefilled from the stored
            // location, so location only counts as changed when the user actually
            // edited it — a no-op save preserves the stored string verbatim rather than
            // re-normalizing/upper-casing a non-canonical or non-US location.
            const original = splitLocation(c?.location ?? '');
            const locationChanged = form.city !== original.city || form.state !== original.state;
            const phoneChanged = (c?.phone ?? '').trim() !== form.phone.trim();
            if (c && (phoneChanged || locationChanged)) {
                await contactUpsert.mutateAsync({
                    id: c.id,
                    companyId: tenantId,
                    firstName: c.firstName,
                    lastName: c.lastName,
                    title: c.title,
                    phone: form.phone.trim(),
                    location: locationChanged ? joinLocation(form.city, form.state) : c.location ?? '',
                    organization: c.organization,
                    // Preserve the stored type; fall back to the same default as a new
                    // contact for the (NOT NULL, so unreachable) empty case.
                    contactType: c.contactType || 'GOVERNMENT',
                    isActive: c.isActive
                });
            }
        }
        const roleValue = formIsOtherRole ? form.customRole.trim() || 'Other' : selectedRole?.code ?? '';
        await upsert.mutateAsync({
            id: '',
            programId,
            contactId,
            role: roleValue,
            customerPocRoleId: form.customerPocRoleId
        });
        setFormOpen(false);
        resetForm();
    };
    // The drawer's inline edit for an existing assignment: reassign the contact
    // (parent state) and/or change the POC role (RHF). Phone/location writeback
    // and new-contact creation stay in the Add dialog — editing an assignment is
    // scoped to the assignment's own fields.
    const submitContactEdit = async (values, id)=>{
        const contactId = editContactId;
        if (!contactId) throw new Error('Select a contact.');
        const roleId = String(values.customerPocRoleId ?? '');
        if (!roleId) throw new Error('Select a customer POC role.');
        const role = roles.find((v)=>v.id === roleId);
        const roleValue = isOtherRole(role) ? String(values.customRole ?? '').trim() || 'Other' : role?.code ?? '';
        await upsert.mutateAsync({
            id,
            programId,
            contactId,
            role: roleValue,
            customerPocRoleId: roleId
        });
    };
    const handleDelete = async ()=>{
        if (!deleting) return;
        await remove.mutateAsync(deleting.id);
        setDeleting(null);
    };
    const busy = upsert.isPending || contactUpsert.isPending;
    // Self-labeled contact control for the drawer's inline edit (dropped into its
    // Contact row via editControl). Parent-controlled, so a pick never resets the
    // POC-role form fields. "+ New contact" is intentionally absent — reassigning
    // an existing assignment picks among registry contacts, not creates one. The
    // trigger keeps id="contactId" so it reads as the "Contact" field.
    const contactEditControl = /*#__PURE__*/ _jsxs("div", {
        className: "space-y-1.5",
        children: [
            /*#__PURE__*/ _jsx(Label, {
                htmlFor: "contactId",
                children: "Contact"
            }),
            /*#__PURE__*/ _jsx(SpecraCombobox, {
                id: "contactId",
                label: "Contact",
                options: contactOptions,
                value: editContactId || undefined,
                onValueChange: (v)=>setEditContactId(v ?? ''),
                placeholder: "Select a contact…",
                searchPlaceholder: "Search contacts by name or email…"
            })
        ]
    });
    const detailEdit = detail ? {
        defaultValues: {
            customerPocRoleId: detail.customerPocRoleId ?? '',
            customRole: isOtherRole(roles.find((v)=>v.id === detail.customerPocRoleId)) ? detail.role ?? '' : ''
        },
        onSubmit: (v)=>submitContactEdit(v, detail.id),
        isSubmitting: busy,
        submitLabel: 'Save changes',
        onEditStart: ()=>setEditContactId(detail.contactId)
    } : undefined;
    const roleOptions = roles.map((v)=>({
            value: v.id,
            label: v.displayLabel
        }));
    const detailSections = detail ? (()=>{
        const c = contactById.get(detail.contactId);
        const role = roles.find((v)=>v.id === detail.customerPocRoleId);
        const other = isOtherRole(role);
        const roleLabel = other && detail.role && detail.role !== 'Other' ? detail.role : detail.customerPocRoleLabel || detail.role || '—';
        return [
            {
                heading: 'Contact',
                rows: [
                    {
                        label: 'Contact',
                        value: detail.contactName || (c ? `${c.firstName} ${c.lastName}`.trim() : '—') || '—',
                        editControl: contactEditControl
                    },
                    {
                        label: 'Email',
                        value: detail.contactEmail || c?.email || '—'
                    },
                    {
                        label: 'Phone #',
                        value: c?.phone || '—'
                    },
                    {
                        label: 'Location',
                        value: c?.location || '—'
                    }
                ]
            },
            {
                heading: 'Role',
                rows: [
                    {
                        label: 'Customer POC role',
                        value: roleLabel,
                        editField: {
                            kind: 'select',
                            name: 'customerPocRoleId',
                            label: 'Customer POC role',
                            options: roleOptions,
                            searchable: true
                        }
                    },
                    // Always present in edit mode so switching to "Other" can be named
                    // in place; reads '—' for a standard role.
                    {
                        label: 'Custom role name',
                        value: other ? detail.role || '—' : '—',
                        editField: {
                            kind: 'text',
                            name: 'customRole',
                            label: 'Custom role name',
                            maxLength: 50
                        }
                    }
                ]
            }
        ];
    })() : [];
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsx(SectionNote, {
                children: "Contacts are the people outside Sprezz on this contract — government POCs (CO, COR, contract specialists) and partner or subcontractor staff. Sprezz team members are recorded on the Roles tab."
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "flex justify-end",
                children: /*#__PURE__*/ _jsx(SpecraButton, {
                    onClick: openAdd,
                    disabled: !canWrite,
                    title: !canWrite ? PROGRAM_WRITE_DENIED.roster : undefined,
                    "data-testid": Testids.deliveryProgramRel.contactCreate,
                    children: "Add Contact"
                })
            }),
            isLoading || contactGroups.length === 0 ? /*#__PURE__*/ _jsx(EntityTable, {
                data: items,
                columns: columns,
                testid: Testids.deliveryProgramRel.contactsTable,
                rowTestid: Testids.deliveryProgramRel.contactRow,
                rowKey: (c)=>c.id,
                isLoading: isLoading,
                hideRowActions: !canWrite && !canManageContacts,
                onRowClick: handleContactRowClick,
                rowActions: contactRowActions
            }) : // One EntityTable per affiliation group (Unaffiliated last), each
            // wrapped in a collapsible header — reuses the shared table for every
            // group's rows rather than forking a second table implementation.
            /*#__PURE__*/ _jsx(Accordion, {
                type: "multiple",
                defaultValue: contactGroups.map((g)=>g.label),
                children: contactGroups.map((group)=>/*#__PURE__*/ _jsxs(AccordionItem, {
                        value: group.label,
                        children: [
                            /*#__PURE__*/ _jsx(AccordionTrigger, {
                                "data-testid": Testids.deliveryProgramRel.programContactGroupHeader,
                                "data-affiliation": group.label,
                                children: /*#__PURE__*/ _jsxs("span", {
                                    className: "flex items-center gap-2",
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "font-medium",
                                            children: group.label
                                        }),
                                        /*#__PURE__*/ _jsxs("span", {
                                            className: "text-xs text-muted-foreground",
                                            children: [
                                                "(",
                                                group.rows.length,
                                                ")"
                                            ]
                                        })
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx(AccordionContent, {
                                children: /*#__PURE__*/ _jsx(EntityTable, {
                                    data: group.rows,
                                    columns: columns,
                                    testid: Testids.deliveryProgramRel.contactsTable,
                                    rowTestid: Testids.deliveryProgramRel.contactRow,
                                    rowKey: (c)=>c.id,
                                    hideRowActions: !canWrite && !canManageContacts,
                                    onRowClick: handleContactRowClick,
                                    rowActions: contactRowActions
                                })
                            })
                        ]
                    }, group.label))
            }),
            !canManageContacts && /*#__PURE__*/ _jsx("p", {
                id: deleteHintId,
                className: "text-xs text-muted-foreground",
                children: "Only a program manager can remove a contact from this program."
            }),
            /*#__PURE__*/ _jsx(EntityFormDialog, {
                open: formOpen,
                onOpenChange: setFormOpen,
                title: "Add Contact to Program",
                fields: [],
                defaultValues: {},
                onSubmit: handleSubmit,
                isSubmitting: busy,
                topContent: /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-4",
                    children: [
                        /*#__PURE__*/ _jsx(LabeledField, {
                            id: "contactId",
                            label: "Contact",
                            required: true,
                            children: /*#__PURE__*/ _jsx(SpecraCombobox, {
                                id: "contactId",
                                label: "Contact",
                                options: [
                                    {
                                        value: NEW_CONTACT,
                                        label: '+ New contact…'
                                    },
                                    ...contactOptions
                                ],
                                value: form.contactId || undefined,
                                onValueChange: (v)=>selectContact(v ?? ''),
                                placeholder: "Select a contact…",
                                searchPlaceholder: "Search contacts by name or email…"
                            })
                        }),
                        isNewContact && /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "grid grid-cols-2 gap-3",
                                    children: [
                                        /*#__PURE__*/ _jsx(LabeledField, {
                                            id: "firstName",
                                            label: "First name",
                                            required: true,
                                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                                id: "firstName",
                                                value: form.firstName,
                                                onChange: (e)=>setField('firstName', e.target.value)
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(LabeledField, {
                                            id: "lastName",
                                            label: "Last name",
                                            required: true,
                                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                                id: "lastName",
                                                value: form.lastName,
                                                onChange: (e)=>setField('lastName', e.target.value)
                                            })
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(LabeledField, {
                                    id: "email",
                                    label: form.contactType === 'GOVERNMENT' ? 'Government or agency email' : 'Company or corporate email',
                                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                                        id: "email",
                                        type: "email",
                                        "data-testid": Testids.deliveryProgramRel.contactEmailInput,
                                        value: form.email,
                                        onChange: (e)=>setField('email', e.target.value)
                                    })
                                }),
                                /*#__PURE__*/ _jsx(LabeledField, {
                                    id: "contactType",
                                    label: "Contact type",
                                    children: /*#__PURE__*/ _jsxs(Select, {
                                        value: form.contactType,
                                        onValueChange: (v)=>setField('contactType', v),
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                                id: "contactType",
                                                children: /*#__PURE__*/ _jsx(SelectValue, {})
                                            }),
                                            /*#__PURE__*/ _jsx(SelectContent, {
                                                children: CONTACT_TYPE_OPTIONS.filter((o)=>o.value !== 'INTERNAL').map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                        value: o.value,
                                                        children: o.label
                                                    }, o.value))
                                            })
                                        ]
                                    })
                                })
                            ]
                        }),
                        form.contactId && /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(LabeledField, {
                                    id: "phone",
                                    label: "Phone #",
                                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                                        id: "phone",
                                        value: form.phone,
                                        placeholder: "+1 555-0100",
                                        onChange: (e)=>setField('phone', e.target.value)
                                    })
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "grid grid-cols-[1fr_5rem] gap-3",
                                    children: [
                                        /*#__PURE__*/ _jsx(LabeledField, {
                                            id: "city",
                                            label: "City",
                                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                                id: "city",
                                                value: form.city,
                                                placeholder: "Los Angeles",
                                                onChange: (e)=>setField('city', e.target.value)
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(LabeledField, {
                                            id: "state",
                                            label: "State",
                                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                                id: "state",
                                                value: form.state,
                                                maxLength: 2,
                                                placeholder: "CA",
                                                className: "uppercase",
                                                // Letters only, so a two-letter state abbreviation can't
                                                // become "City, 1A".
                                                onChange: (e)=>setField('state', e.target.value.replace(/[^a-zA-Z]/g, '').toUpperCase())
                                            })
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(LabeledField, {
                            id: "customerPocRoleId",
                            label: "Customer POC role",
                            required: true,
                            children: /*#__PURE__*/ _jsx(SpecraCombobox, {
                                id: "customerPocRoleId",
                                label: "Customer POC role",
                                options: roleOptions,
                                value: form.customerPocRoleId || undefined,
                                onValueChange: (v)=>setField('customerPocRoleId', v ?? ''),
                                placeholder: "Select a role…",
                                searchPlaceholder: "Search roles…"
                            })
                        }),
                        formIsOtherRole && /*#__PURE__*/ _jsx(LabeledField, {
                            id: "customRole",
                            label: "Role name",
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                id: "customRole",
                                value: form.customRole,
                                maxLength: 50,
                                placeholder: "Type the role",
                                onChange: (e)=>setField('customRole', e.target.value)
                            })
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(ConfirmDeleteDialog, {
                open: !!deleting,
                onOpenChange: (open)=>!open && setDeleting(null),
                title: "Remove Contact",
                description: deleting ? `Remove "${deleting.contactName}" from this program? This cannot be undone.` : undefined,
                onConfirm: handleDelete,
                isPending: remove.isPending
            }),
            /*#__PURE__*/ _jsx(ScoreDetailDrawer, {
                open: !!detail,
                onOpenChange: (open)=>!open && setDetailId(null),
                title: detail ? detail.contactName || (contactById.get(detail.contactId) ? `${contactById.get(detail.contactId)?.firstName} ${contactById.get(detail.contactId)?.lastName}`.trim() : '') || 'Contact' : '',
                sections: detailSections,
                testid: Testids.deliveryProgramRel.contactDetailDrawer,
                edit: detailEdit,
                canEdit: canWrite,
                initialMode: detailMode
            })
        ]
    });
}
